import { useAuth } from '@/hooks';
import { useLocation } from 'react-router-dom';
import { useMailBox } from '@/hooks/use-mailbox';
import { useIsExternalUser } from './useIsExternalUser';
import { useCheckExtensionInstalled } from '@/hooks/use-check-extension-installed';
import { useAtsPermissions } from './useAtsPermissions';

export const useNudges = () => {
  const { currUser } = useAuth();
  const { pathname } = useLocation();
  const isExternalUser = useIsExternalUser();
  const extResult = useCheckExtensionInstalled();

  const isFlowsRoute = pathname.startsWith('/flows');
  const isDashboardRoute = pathname.startsWith('/dashboard');
  const isTasksRoute = pathname.startsWith('/tasks');
  const {
    isFlowEnabled,
    jobPermissions,
    contactsPermissions,
    flowsPermissions,
  } = useAtsPermissions();

  const { isMailBoxConnected, isCalConnected } = useMailBox();

  const { status = {}, installed } = extResult || {};
  const isLinkedinErrorRoute =
    !(!installed || !status || !Object.values(status)?.length) &&
    (isDashboardRoute || isTasksRoute);

  const {
    login_li_pub,
    feat_li_rec,
    login_li_rec,
    rlt_in_li_inmail = {},
    rlt_in_li_con_req = {},
    lt_inmail_credit,
  } = status || {};

  const { li: inmailLi, kula_daily: inmailKulaDaily } = rlt_in_li_inmail;
  const { li: conReqLi, kula_weekly: conReqKulaWeekly } = rlt_in_li_con_req;

  const { li_settings = {} } = status;
  const { email, notification, notifFrequency, allMessageNotification } =
    li_settings;

  const {
    account,
    google_auth_error_code: googleAuthErrorCode,
    ms_auth_error_code: msAuthErrorCode,
  } = currUser || {};

  const { gcal: gcalErrorCode, mscal: mscalErrorCode } =
    currUser?.auth_error_code || {};

  const { status: accountStatus } = account || {};

  const liSettingPreCheck = installed && login_li_pub && login_li_rec;

  const showExtensionNudge =
    !installed &&
    !isFlowsRoute &&
    isFlowEnabled &&
    (jobPermissions.index ||
      flowsPermissions.index ||
      contactsPermissions.index);

  const hasLiSettingsConfigured = ![
    email,
    notification,
    notifFrequency,
    allMessageNotification,
  ].includes(false);

  const activeNudges = [
    {
      id: 'googleReconnect',
      condition:
        !isMailBoxConnected &&
        googleAuthErrorCode >= 400 &&
        googleAuthErrorCode <= 500,
    },
    { id: 'kulaExtension', condition: showExtensionNudge },
    {
      id: 'linkedinSync',
      condition: liSettingPreCheck && !hasLiSettingsConfigured,
    },
    {
      id: 'microsoftReconnect',
      condition:
        !isMailBoxConnected && msAuthErrorCode >= 400 && msAuthErrorCode <= 500,
    },
    {
      id: 'isAccountLoggedOut',
      condition: !login_li_pub && isLinkedinErrorRoute,
    },
    {
      id: 'isRecruiterLoggedOut',
      condition: feat_li_rec && !login_li_rec && isLinkedinErrorRoute,
    },
    {
      id: 'isInMailLimitOver',
      condition:
        feat_li_rec &&
        login_li_pub &&
        login_li_rec &&
        inmailLi &&
        isLinkedinErrorRoute,
    },
    {
      id: 'isInternalInMailLimitOver',
      condition:
        feat_li_rec &&
        login_li_pub &&
        login_li_rec &&
        inmailKulaDaily &&
        isLinkedinErrorRoute,
    },
    {
      id: 'isConnectionRequestOver',
      condition: login_li_pub && conReqLi && isLinkedinErrorRoute,
    },
    {
      id: 'isInternalConWeeklyReqOver',
      condition: login_li_pub && conReqKulaWeekly && isLinkedinErrorRoute,
    },
    {
      id: 'isInMailCreditsOver',
      condition:
        feat_li_rec &&
        login_li_pub &&
        login_li_rec &&
        lt_inmail_credit &&
        isLinkedinErrorRoute,
    },
    {
      id: 'outlookCalReconnect',
      condition:
        !isCalConnected && mscalErrorCode >= 400 && mscalErrorCode <= 500,
    },
    {
      id: 'gmailCalReconnect',
      condition:
        !isCalConnected && gcalErrorCode >= 400 && gcalErrorCode <= 500,
    },
    { id: 'trailUser', condition: accountStatus === 'trial' },
  ]
    .filter(({ condition }) => condition)
    .map(({ id }) => ({ id }));

  return {
    activeNudges: isExternalUser ? [] : activeNudges,
  };
};
